'use client';

import { css, cx } from '@repo/pandacss/styled-system/css';
import { type ReactNode, useEffect, useRef, useState } from 'react';

type Props = {
  children: ReactNode;
  className?: string;
};

export default function SummaryTableContainer({ children, className }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showFade, setShowFade] = useState({ left: false, right: false });

  useEffect(() => {
    const containerElement = containerRef.current;

    const handleScroll = () => {
      if (!containerElement) return;

      const { scrollLeft, scrollWidth, clientWidth } = containerElement;
      setShowFade({
        left: scrollLeft > 0,
        right: scrollLeft < scrollWidth - clientWidth,
      });
    };

    handleScroll();

    if (containerElement) {
      containerElement.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
    }

    return () => {
      if (containerElement) {
        containerElement.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      }
    };
  }, []);

  const getFadeGradient = () => {
    if (showFade.left && showFade.right) {
      return 'linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 0.7) 5%, rgba(0, 0, 0, 1) 10%, rgba(0, 0, 0, 0.7) 95%, transparent 100%)';
    }
    if (!showFade.left && showFade.right) {
      return 'linear-gradient(to left, transparent, black 5%)';
    }
    if (showFade.left && !showFade.right) {
      return 'linear-gradient(to right, transparent, black 5%)';
    }
    return 'none';
  };

  return (
    <div
      className={css({
        position: 'relative',
        maxWidth: 'full',
      })}
    >
      <div
        ref={containerRef}
        className={cx(
          css({
            overflowX: { base: 'auto', md: 'hidden' },
            overflowY: 'hidden',
            width: 'full',
            position: 'relative',
          }),
          className,
        )}
        style={
          {
            '--fade': getFadeGradient(),
            maskImage: 'var(--fade)',
            WebkitMaskImage: 'var(--fade)',
          } as React.CSSProperties
        }
      >
        {children}
      </div>
    </div>
  );
}
